import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import P_card from '../../Presets/preset-card';
import { ArrowRight, Heart, Share2, ShoppingBag } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import './newSections.css';
import SectionSeparator from '../../Homepage/SectionSeparator';

// ─── Image imports (reusing existing sample assets) ───────────────────────────
import kurta_e from '../../assets/samples/Types/kurta.jpg';
import suit_e from '../../assets/samples/Types/suit.jpg';
import shirt_pant_e from '../../assets/samples/Types/shirt_pant.jpg';
import sadri_e from '../../assets/samples/Types/CUSTOM SADRI.jpg';
import shirt_e from '../../assets/samples/Types/shirt.png';
import pant_e from '../../assets/samples/Types/pant.jpg';
import sherwani_e from '../../assets/samples/Types/sherwani.jpg';
import jubba_e from '../../assets/samples/Types/jubba.jpg';
import blazer_e from '../../assets/samples/Types/blazer.jpg';

// ─── Data definitions (sourced from client UI.xlsx) ──────────────────────────

const SET_CATEGORIES = [
  {
    id: 'pathani-kurta',
    name: 'Pathani Kurta',
    image: kurta_e,
    status: 'launch',
    types: [1, 0, 1, 0],
    navigateTo: null,
  },
  {
    id: 'designer-kurta',
    name: 'Designer Kurta',
    image: suit_e,
    status: 'launch',
    types: [1, 0, 1, 0],
    navigateTo: null,
  },
  {
    id: 'dhoti-kurta',
    name: 'Dhoti Kurta',
    image: shirt_pant_e,
    status: 'launch',
    types: [1, 0, 1, 0],
    navigateTo: null,
  },
];

const SINGLE_CATEGORIES = [
  {
    id: 'kurta-coat',
    name: 'Kurta with Coat',
    image: sadri_e,
    status: 'launch',
    navigateTo: 'kurta',
  },
  {
    id: 'wedding-kurta',
    name: 'Wedding Kurta',
    image: sherwani_e,
    status: 'launch',
    navigateTo: 'kurta',
  },
  {
    id: 'short-kurta-pajama',
    name: 'Short Kurta Pajama',
    image: pant_e,
    status: 'launch',
    navigateTo: 'kurta',
  },
  {
    id: 'kurta-churidaar',
    name: 'Kurta Churidaar',
    image: kurta_e,
    status: 'launch',
    navigateTo: 'kurta',
  },
];

// Combine all categories for the swiper
const ALL_CATEGORIES = [...SET_CATEGORIES, ...SINGLE_CATEGORIES];

// Helper to map our category data to P_card props
const mapToPCard = (item) => ({
    ...item,
    src: item.image,
    price: 0, // Force price 0 to potentially hide or minimize
    discount: 0,
    images: [item.image],
    category: item.id
});

// ─── Component ────────────────────────────────────────────────────────────────

function CategorySelector({ onSelectSet, onSelectSingle, selectedId, process }) {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState(null);

  // Helper to render a card using P_card
  const renderCard = (item) => {
    const isComing = item.status === 'coming';
    const pCardItem = mapToPCard(item);
    
    return (
      <div 
        key={item.id}
        className={`cs-base-style-card-wrapper ${isComing ? 'cs-card--coming' : ''}`}
        style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '12px', overflow: 'hidden' }}
        onClick={(e) => {
             // Prevent P_card navigation if we want to handle selection
             e.preventDefault(); 
             e.stopPropagation();
             if (!isComing) {
                if (item.types) {
                    onSelectSet(item);
                    setTimeout(() => {
                        document.getElementById('cs-subcategory-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                } else {
                     if (item.navigateTo) {
                        navigate(`/custom/${item.navigateTo}/select_cloth/${process}`);
                    }
                    onSelectSingle(item);
                    setTimeout(() => {
                        document.getElementById('cs-pairing-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                }
            }
        }}
      >
        <div style={{ pointerEvents: 'none' }}>
             {/* Force hide extra elements using CSS module or inline styles via class overrides */}
             <style>{`
                .cs-base-style-card-wrapper .product-card-delivery,
                .cs-base-style-card-wrapper .product-card-price-row,
                .cs-base-style-card-wrapper .product-card-prepaid,
                .cs-base-style-card-wrapper .product-card-badge,
                .cs-base-style-card-wrapper .product-card-customize-badge,
                .cs-base-style-card-wrapper .product-card-discount-badge {
                    display: none !important;
                }
                .cs-base-style-card-wrapper .product-card-info {
                    padding-bottom: 12px !important;
                }
             `}</style>
             <P_card item={pCardItem} />
        </div>
        {isComing && <div className="cs-coming-overlay" />}
      </div>
    );
  };

  const swiperSettings = {
    modules: [Autoplay, Navigation],
    spaceBetween: 20,
    slidesPerView: 2.2, // Mobile default (2 cards)
    loop: true,
    speed: 1000,
    autoplay: {
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
    },
    breakpoints: {
        640: { slidesPerView: 3.2 },
        768: { slidesPerView: 5 }, // Tablet (5 cards)
        1024: { slidesPerView: 7 }, // Desktop (7 cards)
        1280: { slidesPerView: 7 }
    },
    className: "cs-swiper"
  };

  return (
    <section className="cs-category-section">
      <div className="cs-container-full"> {/* Full width container for swiper */}

        {/* ── Section Header ── */}
        <div className="cs-section-header" style={{ marginBottom: '60px', marginTop: '-10px' }}>
          <SectionSeparator title="Start with a base style" />
        </div>

        {/* ── Swiper Carousel ── */}
        <div className="cs-swiper-container">
            <Swiper {...swiperSettings}>
                {ALL_CATEGORIES.map((item) => (
                    <SwiperSlide key={item.id}>
                        {renderCard(item)}
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>

        {/* ── Design Process Section (New) ── */}
        <div className="cs-design-process">
            <SectionSeparator title="Design your custom suit" />
            
            <div className="cs-process-grid">
                {/* Step 1: Select Fabric */}
                <div className="cs-process-item">
                    <img 
                        src="https://cdn-icons-png.flaticon.com/512/3389/3389061.png" 
                        alt="Fabric Icon" 
                        className="cs-process-icon" 
                        style={{ opacity: 0.8 }}
                    />
                    <h3 className="cs-process-title">SELECT THE FABRIC</h3>
                    <p className="cs-process-desc">
                        New trendy suit fabrics every season. We offer a wide range of fabrics for every season so you can be on-trend in a affordable and eco-friendly way.
                    </p>
                </div>

                {/* Step 2: Design Tailored Suit */}
                <div className="cs-process-item">
                    <img 
                        src="https://cdn-icons-png.flaticon.com/512/9385/9385213.png" 
                        alt="Jacket Icon" 
                        className="cs-process-icon" 
                        style={{ opacity: 0.8 }}
                    />
                    <h3 className="cs-process-title">DESIGN YOUR TAILORED SUIT</h3>
                    <p className="cs-process-desc">
                        Personalize details on your suit such as lapels, pockets, lining. Explore all of our options online.
                    </p>
                </div>

                {/* Step 3: Get Measured */}
                <div className="cs-process-item">
                    <img 
                        src="https://cdn-icons-png.flaticon.com/512/8051/8051310.png" 
                        alt="Tape Measure Icon" 
                        className="cs-process-icon" 
                        style={{ opacity: 0.8 }}
                    />
                    <h3 className="cs-process-title">GET MEASURED</h3>
                    <p className="cs-process-desc">
                        Take your measurements for your suit wherever you are. You don't need a tailor, you just need a friend and a measuring tape.
                    </p>
                </div>
            </div>
        </div>

      </div>
    </section>
  );
}

export default CategorySelector;
export { SET_CATEGORIES, SINGLE_CATEGORIES };
